import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import messages from './messages';
import { useStyles } from './styles';
import { OFFER, OPENING, SubjectType } from './types';

interface SubjectTypeToggleProps {
  value: SubjectType;
  onChange: (next: SubjectType) => void;
}

const SIDES: Array<{ value: SubjectType; label: MessageDescriptor }> = [
  { value: OFFER, label: messages.offersTab },
  { value: OPENING, label: messages.openingsTab },
];

/**
 * The segmented [ Offers | Openings ] filter over the rule table
 * (Design Requirements §2).
 *
 * A radio group wearing segments, not the `Tabs` strip this replaced and not a
 * pair of toggle buttons. Three things follow from the choice, and the third is
 * why it is worth the hand-styling:
 *
 *   `layout-composition` separates the patterns — tab navigation for "sections
 *     switchable without a URL change", a segment filter for "filtering the
 *     primary content view". These two sides are one entity-agnostic table
 *     filtered by `subject_type`, so this is the second. It also leaves
 *     `role="tablist"` to the page's own strip, whose other two tabs are still
 *     to be built.
 *   The table always lists one side or the other, which is the radio contract
 *     exactly: one of a set, never none. Toggle buttons carry `aria-pressed`
 *     and permit "all off", so an exclusive group has to *guard* against a
 *     re-click deselecting the only choice. Here that state cannot be
 *     expressed, so there is nothing to guard.
 *   A radio group is one tab stop and the arrow keys move within it — the
 *     keyboard behaviour the `Tabs` strip already had. A pair of buttons is two
 *     tab stops with no arrow keys, which would have been a regression.
 *
 * The radios themselves are visually hidden rather than `display: none`, which
 * would take the inputs out of the accessibility tree along with the glyphs and
 * leave a group that no keyboard can reach. Each label is the visible segment.
 */
export const SubjectTypeToggle: React.FC<SubjectTypeToggleProps> = ({
  value,
  onChange,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  // No `name` on the group: `RadioGroup` mints a unique one per instance, and a
  // hardcoded one would make every instance on a page a single *native* radio
  // group — the browser groups radios by name, so checking a side in one would
  // uncheck it in the others and leave React's controlled value arguing with the
  // DOM. Measured on a harness rendering five copies: 1 of 10 inputs checked.
  return (
    <RadioGroup
      aria-label={intl.formatMessage(messages.subjectTypeLabel)}
      className={styles.subjectToggle}
      row
      value={value}
      onChange={event => onChange(event.target.value as SubjectType)}
    >
      {SIDES.map(side => (
        <FormControlLabel
          className={classNames(styles.subjectToggleSegment, {
            [styles.subjectToggleSegmentSelected]: side.value === value,
          })}
          // `disableRipple` because the ripple draws on the hidden control, so
          // it would animate inside a 1px box the segment does not contain.
          control={
            <Radio className={styles.subjectToggleRadio} disableRipple />
          }
          key={side.value}
          label={intl.formatMessage(side.label)}
          value={side.value}
        />
      ))}
    </RadioGroup>
  );
};
